/**
 * The wire contract between server and web client.
 *
 * Both workspaces import this file directly (no build step), so the two sides
 * cannot drift: changing a message shape here is a compile error on whichever
 * side hasn't caught up.
 */

export type Side = "white" | "black";

/**
 * "1v1" is one player per side. "democracy" is Team chess: many players per side, where
 * each member proposes a move. The protocol is
 * deliberately identical for both, because clients always *propose* moves and
 * let the server decide when one commits.
 */
export type GameMode = "1v1" | "democracy";

/** How a Team chess team's locked ballots resolve at the end of a turn. */
export type DemocracyVoteMode = "random" | "winner-takes-all" | "popcorn";

export const DEMOCRACY_VOTE_MODES: { mode: DemocracyVoteMode; name: string; blurb: string }[] = [
  { mode: "random", name: "Lottery mode", blurb: "Choose one locked ballot at random." },
  {
    mode: "winner-takes-all",
    name: "Democracy mode",
    blurb: "Play the move with the most votes; tied moves are chosen at random.",
  },
  {
    mode: "popcorn",
    name: "Popcorn mode",
    blurb: "Teammates take turns in a set order, one move each, then round again.",
  },
];

/** Popcorn mode is the one rule where a single named player owns each turn. */
export function isPopcorn(mode: DemocracyVoteMode | null): boolean {
  return mode === "popcorn";
}

export type GameStatus = "waiting" | "active" | "finished";

/** Winner of a finished game, or "draw". Null while the game is still running. */
export type GameResult = Side | "draw" | null;

export type GameResultReason =
  | "checkmate"
  | "stalemate"
  | "resignation"
  | "threefold_repetition"
  | "insufficient_material"
  | "fifty_move_rule"
  | "draw_agreed"
  | "timeout";

/** Bounds on time controls, shared so the client can't offer what the server rejects. */
export const MAX_INITIAL_SECONDS = 3600;
export const MAX_INCREMENT_SECONDS = 60;
export const MIN_INITIAL_SECONDS = 10;

export interface TimeControl {
  /** Starting time per side, in seconds. */
  initialSeconds: number;
  /** Seconds added to a player's clock after each of their moves. */
  incrementSeconds: number;
}

export interface ClockDto extends TimeControl {
  whiteMs: number;
  blackMs: number;
  /**
   * Side whose clock is currently running, or null if nothing is ticking
   * (game not started, or already over). Clients count down locally from the
   * moment this arrives rather than trusting their own wall clock.
   */
  runningFor: Side | null;
}

export interface UserDto {
  id: string;
  username: string;
}

/**
 * Stockfish strength offered to a player. These are deliberately broad,
 * friendly presets rather than exposing an engine's raw technical settings.
 */
export type BotLevel = 1 | 2 | 3 | 4 | 5;

export const BOT_LEVELS: { level: BotLevel; name: string; blurb: string }[] = [
  { level: 1, name: "Level 1", blurb: "A relaxed beginner-friendly opponent." },
  { level: 2, name: "Level 2", blurb: "A casual game with occasional tactics." },
  { level: 3, name: "Level 3", blurb: "A capable club-player challenge." },
  { level: 4, name: "Level 4", blurb: "A sharp, strong opponent." },
  { level: 5, name: "Level 5", blurb: "The toughest local Stockfish setting." },
];

export interface PlayerDto {
  userId: string;
  username: string;
  side: Side;
  /** Only meaningful while a democracy game is waiting to start. */
  ready: boolean;
  /** Bots are real players; this only changes how they're labelled and driven. */
  isBot: boolean;
  botLevel: BotLevel | null;
  /**
   * How often this player's ballots became the team's move. Counts only rounds
   * that have resolved, so an open round doesn't drag the figure down. Null
   * outside democracy chess, where there is nothing to select between.
   */
  ballots: { cast: number; chosen: number } | null;
  /**
   * Position within this player's team rotation, ascending. Only popcorn mode
   * acts on it, but it is maintained for every team game so switching vote
   * modes in the lobby never needs a backfill.
   */
  turnOrder: number;
}

export interface MoveDto {
  /** 1-based half-move number. Unique per game — this is what serializes commits. */
  ply: number;
  /** Standard algebraic notation, e.g. "Nf3", "exd5", "O-O". */
  san: string;
  /** Long algebraic / UCI, e.g. "g1f3", "e7e8q". */
  uci: string;
  userId: string;
  at: string;
}

/** A locked ballot. It is disclosed only once its round has resolved. */
export interface DemocracyProposalDto {
  userId: string;
  username: string;
  uci: string;
  san: string;
  chosen: boolean;
}

/** The public audit record for one resolved democracy-chess turn. */
export interface DemocracyResolutionDto {
  /** The resulting 1-based chess ply. */
  ply: number;
  side: Side;
  proposals: DemocracyProposalDto[];
}

/**
 * State specific to democracy chess. Current ballots reveal only that a player
 * has voted, never the move itself; resolved rounds reveal the full record.
 */
export interface DemocracyStateDto {
  /** Current lobby readiness, represented on PlayerDto.ready. */
  readyCount: number;
  /** Number of locked ballots still required before this turn resolves. */
  requiredVotes: number;
  /** User IDs that have locked a ballot for the current turn. */
  voters: string[];
  /**
   * In popcorn mode, the one player on the side to move whose turn it is.
   * Null in every other vote mode, where the whole team votes at once.
   */
  activePlayerId: string | null;
  /**
   * The player who will take the side-to-move team's *next* turn, so a team can
   * see who is warming up. Null outside popcorn mode.
   */
  nextPlayerId: string | null;
  /** Most recent first; retained so the vote-result overlay can be reopened. */
  resolutions: DemocracyResolutionDto[];
}

export interface GameDto {
  id: string;
  /** Friendly display name for this game lobby, e.g. "orange-baboons". */
  lobbyName: string;
  /** The lobby owner. They can adjust Democracy settings before the game starts. */
  createdBy: string;
  mode: GameMode;
  /** Null for 1v1 games. */
  democracyVoteMode: DemocracyVoteMode | null;
  status: GameStatus;
  result: GameResult;
  resultReason: GameResultReason | null;
  fen: string;
  /** Side to move. Meaningless once status is "finished". */
  turn: Side;
  /** Side with an outstanding draw offer, if any. Cleared when a move is played. */
  drawOfferedBy: Side | null;
  /** Null for an untimed game. */
  clock: ClockDto | null;
  /**
   * How long the side to move has been on this turn, measured when this
   * snapshot was built. Clients add their own elapsed time since receiving it,
   * the same way clocks work, so a skewed browser clock changes nothing.
   */
  turnElapsedMs: number;
  players: PlayerDto[];
  moves: MoveDto[];
  /** Present only for democracy games. */
  democracy: DemocracyStateDto | null;
  createdAt: string;
  updatedAt: string;
}

/** Summary shown in the lobby list; omits move history. */
export interface GameSummaryDto {
  id: string;
  lobbyName: string;
  /** Used to offer lobby closing only to the creator. */
  createdBy: string;
  mode: GameMode;
  /** Null for 1v1 games. */
  democracyVoteMode: DemocracyVoteMode | null;
  status: GameStatus;
  result: GameResult;
  players: PlayerDto[];
  turn: Side;
  timeControl: TimeControl | null;
  /** Present for democracy games so the public lobby can show team sizes. */
  whiteCount?: number;
  blackCount?: number;
  readyCount?: number;
  updatedAt: string;
}

export interface ProposedMove {
  from: string;
  to: string;
  /** Required only when a pawn reaches the back rank. */
  promotion?: "q" | "r" | "b" | "n";
}

export type ErrorCode =
  | "NOT_AUTHENTICATED"
  | "NOT_FOUND"
  | "NOT_A_PLAYER"
  | "NOT_YOUR_TURN"
  | "ILLEGAL_MOVE"
  | "GAME_OVER"
  | "GAME_FULL"
  | "ALREADY_VOTED"
  | "CONFLICT"
  | "BAD_REQUEST"
  | "INTERNAL";

// ---------------------------------------------------------------------------
// client -> server
// ---------------------------------------------------------------------------

export type ClientMessage =
  | { t: "subscribe"; gameId: string }
  | { t: "unsubscribe"; gameId: string }
  | { t: "propose"; gameId: string; move: ProposedMove }
  | { t: "ready"; gameId: string }
  | { t: "resign"; gameId: string }
  | { t: "offer_draw"; gameId: string }
  | { t: "respond_draw"; gameId: string; accept: boolean }
  | { t: "ping" };

// ---------------------------------------------------------------------------
// server -> client
// ---------------------------------------------------------------------------

export type ServerMessage =
  /** Full snapshot. Sent on every subscribe, which is what makes reconnect trivial. */
  | { t: "state"; game: GameDto }
  | { t: "moved"; gameId: string; move: MoveDto; game: GameDto }
  | { t: "presence"; gameId: string; online: string[] }
  | { t: "error"; gameId?: string; code: ErrorCode; message: string }
  | { t: "pong" };

export const WS_PATH = "/ws";
